/**
 * Round simulator (Phase 40.3 / 40.5 / 40.6 — DESIGN.md "Plan & Resolve rounds").
 *
 * Every order of a round — a shot, a walk, a jump, or any mix per unit — runs
 * through ONE fixed-step loop against the shared, evolving world. Each step
 * advances all live flights with `rk4Step` against the planets *as they are
 * now*, collects that step's impacts, applies them in deterministic order
 * through the pure impact resolver (craters, mass loss, damage, knockback),
 * spawns cluster submunitions, walks planned moves along the surface at walking
 * speed, and steps airborne units (jumps and knockbacks alike) so their
 * hitboxes move — the owner's rule: an airborne unit is a valid hitbox,
 * especially for a second knockback. Later steps of every other flight bend
 * around what just landed; a unit that walks away in time is missed.
 *
 * Shots leave from where the shooter stood when the order was given (the
 * `launch` snapshot), never from where its own planned walk takes it.
 *
 * `launchStaggerSteps` selects the mode: 0 = true simultaneous (every order
 * starts together); ≥ the longest possible flight = fixed-order resolution.
 *
 * Pure: (state, orders, options) → a round record (per-flight points, impact
 * events with their step, walk and airborne paths, the final state). Same
 * inputs on every peer give the same record, so multiplayer sends only orders.
 */
import type { GameState, PlanetState, PlayerState, FireParams, ProjectileState, Vec3, WeaponKey } from '../types';
import { WEAPONS, PHYS_DT, MAX_PROJECTILE_TIME, OUT_OF_BOUNDS, CLUSTER_LAUNCH_DELAY_STEPS, GEYSER_BOOST_RADIUS, GEYSER_BOOST_VELOCITY, WORMHOLE_COOLDOWN_DIST, WORMHOLE_SPEED_BOOST, MAX_WORMHOLE_TRANSITS, WALK_SPEED } from '../constants';
import { vecSlerp, vecNormalize } from '../math';
import { rk4Step, checkCollision, updateAirbornePlayer, PLAYER_HITBOX_RADIUS } from './physics';
import { buildGeyserSims, type GeyserSim } from './flight';
import { getSurfaceRadius } from './surface';
import { createProjectile, popPendingCluster } from './gameState';
import { applyFireCost, checkFire } from './actions';
import { resolveImpact, queueClusterSubmunitions, type CraterFn, type ImpactResolution } from './resolveImpact';
import { dacos, dhypot } from './dmath';

export interface RoundFireOrder {
  params: FireParams;
  /** Where the shot leaves from, captured when the order was given (defaults to the shooter's position at resolution). */
  launch?: { pos: Vec3; vel: Vec3 };
}

export interface RoundJumpOrder { launchPos: Vec3; launchVel: Vec3 }

/** One unit's plan for the round: any of a shot, a walk to a surface direction, a jump. */
export interface RoundOrder {
  playerId: number;
  fire?: RoundFireOrder;
  walk?: Vec3;
  jump?: RoundJumpOrder;
}

export interface RoundOptions {
  /** Steps between consecutive orders' starts. 0 = all at once; large = one after another. */
  launchStaggerSteps: number;
  /** Hard cap on the round (default: longest flight after the last launch, plus landing time). */
  maxSteps?: number;
  /** Crater implementation (default JS; never pass the WASM one — it mutates WASM terrain). */
  createCraterImpl?: CraterFn;
  /** Ammo and movement were charged when the orders were planned: do not check or charge them here. */
  prepaid?: boolean;
  /**
   * Decision 109: the round runs exactly this many steps, settled or not (an L2 commander playing in pauses), and what
   * is still flying at the end is handed over (`RoundRecord.inFlight`) instead of lost. Absent: classic, "until settled".
   */
  fixedSteps?: number;
  /** Flights carried over from the previous fixed-length round (its `inFlight`): they continue first, in their order. */
  carried?: CarriedFlight[];
  /** Steps in flight before a shot is lost (default MAX_PROJECTILE_TIME, classic's 60 s; the continuous ruleset ends lost shots well before). */
  lostAfterSteps?: number;
}

/** A flight still in the air when a fixed-length round ended: where it is, how it moves, how long it has flown. */
export interface CarriedFlight {
  ownerId: number;
  weaponKey: WeaponKey;
  pos: Vec3;
  vel: Vec3;
  /** Steps it has flown so far (counts toward lostAfterSteps). */
  age: number;
  /** Steps until it launches, for a cluster bomblet not yet released. */
  delay: number;
  cooldownIdx: number;
  transits: number;
  boosted: number[];
}

export type RoundFlightOutcome = 'impact' | 'playerHit' | 'lost' | 'skipped' | 'inFlight';

export interface RoundFlight {
  index: number;
  ownerId: number;
  weaponKey: WeaponKey;
  /** Step the flight entered the world (points[0] is its position then). */
  launchStep: number;
  /** World positions, one per step from launchStep. */
  points: Vec3[];
  endStep: number;
  outcome: RoundFlightOutcome;
  hitPlanetIndex: number;
  hitPoint: Vec3 | null;
  hitPlayerId: number;
  /** Parent flight index for cluster submunitions. */
  submunitionOf: number | null;
  /** Why a shot was skipped (dead shooter, no ammo, hitscan). */
  skipReason?: string;
}

export interface RoundImpactEvent {
  step: number;
  flightIndex: number;
  planetIndex: number;
  point: Vec3;
  airBurst: boolean;
  damages: Array<{ playerId: number; damage: number }>;
  kills: number[];
  launched: number[];
  /** Units a crater rim sheltered from this impact's knockback (37.18). */
  sheltered?: number[];
  terrainChanged: boolean;
  /** The pure resolver's full output for this impact (meshes, logs, drops are applied from it at playback). */
  resolution: ImpactResolution;
}

export interface RoundAirbornePath {
  playerId: number;
  cause: 'knockback' | 'jump';
  fromStep: number;
  points: Vec3[];
  landedStep: number | null;
  landingDamage: number;
  died: boolean;
}

/** A planned walk as it played out: one surface direction per step from fromStep; dirs[last] is where the unit stopped. */
export interface RoundWalkPath {
  playerId: number;
  fromStep: number;
  dirs: Vec3[];
  /** False when the walk was cut short (knocked airborne, killed, round ended). */
  arrived: boolean;
}

export interface RoundRecord {
  flights: RoundFlight[];
  impacts: RoundImpactEvent[];
  airborne: RoundAirbornePath[];
  walks: RoundWalkPath[];
  /** Steps simulated (the round's length). */
  steps: number;
  state: GameState;
  logs: string[];
  /** A fixed-length round only: the flights still in the air at its end, for the next round's `carried`. */
  inFlight?: CarriedFlight[];
}

interface LiveFlight {
  f: RoundFlight;
  /** Steps flown before this round (a carried flight); counts toward the lost-shot cap. */
  age0?: number;
  pos: Vec3;
  vel: Vec3;
  cooldownIdx: number;
  transits: number;
  boosted: Set<number>;
}

interface LiveWalk { playerId: number; target: Vec3; path: RoundWalkPath }

interface Hitbox { playerId: number; planetIndex: number; center: Vec3; surfacePoint: Vec3; airborne: boolean }

const HIT_R2 = PLAYER_HITBOX_RADIUS * PLAYER_HITBOX_RADIUS;

function groundedHitboxes(players: PlayerState[], planets: PlanetState[]): Hitbox[] {
  const out: Hitbox[] = [];
  for (const p of players) {
    if (!p.alive || p.airborne) continue;
    const planet = planets[p.planetIndex];
    if (!planet || !planet.alive) continue;
    const d = p.surfaceDir;
    const len = dhypot(d.x, d.y, d.z);
    if (len < 1e-3) continue;
    const nx = d.x / len, ny = d.y / len, nz = d.z / len;
    const r = getSurfaceRadius(planet, d);
    const sx = planet.position.x + nx * r, sy = planet.position.y + ny * r, sz = planet.position.z + nz * r;
    out.push({ playerId: p.id, planetIndex: p.planetIndex, surfacePoint: { x: sx, y: sy, z: sz }, center: { x: sx + nx * 0.5, y: sy + ny * 0.5, z: sz + nz * 0.5 }, airborne: false });
  }
  return out;
}

function airborneHitboxes(players: PlayerState[], planets: PlanetState[]): Hitbox[] {
  const out: Hitbox[] = [];
  for (const p of players) {
    if (!p.alive || !p.airborne || !p.airbornePos) continue;
    const pos = p.airbornePos;
    out.push({ playerId: p.id, planetIndex: nearestPlanet(pos, planets), surfacePoint: { ...pos }, center: { ...pos }, airborne: true });
  }
  return out;
}

function nearestPlanet(pos: Vec3, planets: PlanetState[]): number {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < planets.length; i++) {
    const p = planets[i];
    if (!p.alive || p.type !== 'planet') continue;
    const d = dhypot(pos.x - p.position.x, pos.y - p.position.y, pos.z - p.position.z) - p.radius;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

/**
 * A round under way (64.3, the live round simulator): everything the batch call kept in locals, so a host can step a
 * round one physics tick at a time (`stepRound`) and read the world between ticks. `simulateRound` is a loop over it,
 * so the batch result and the stepped one are the same, bit for bit.
 */
export interface RoundRun {
  state: GameState;
  readonly opts: RoundOptions;
  readonly logs: string[];
  readonly flights: RoundFlight[];
  readonly live: LiveFlight[];
  readonly pending: Array<{ live: LiveFlight; at: number }>;
  readonly pendingWalks: Array<{ playerId: number; target: Vec3; at: number }>;
  readonly pendingJumps: Array<{ playerId: number; jump: RoundJumpOrder; at: number }>;
  readonly walks: LiveWalk[];
  readonly walkPaths: RoundWalkPath[];
  readonly impacts: RoundImpactEvent[];
  readonly airPaths: Map<number, RoundAirbornePath>;
  readonly airborne: RoundAirbornePath[];
  readonly geysers: GeyserSim[];
  readonly flightCap: number;
  readonly maxSteps: number;
  grounded: Hitbox[];
  groundedDirty: boolean;
  /** The next step to simulate (the round's length so far). */
  step: number;
  /** Settled or capped: stepRound does nothing more. */
  done: boolean;
}

function endFlight(run: RoundRun, lf: LiveFlight, step: number, outcome: RoundFlightOutcome, planetIndex: number, point: Vec3 | null, playerId: number): void {
  lf.f.endStep = step; lf.f.outcome = outcome; lf.f.hitPlanetIndex = planetIndex; lf.f.hitPoint = point; lf.f.hitPlayerId = playerId;
  const i = run.live.indexOf(lf);
  if (i >= 0) run.live.splice(i, 1);
}
function airPathFor(run: RoundRun, id: number, step: number, cause: 'knockback' | 'jump'): RoundAirbornePath {
  let ap = run.airPaths.get(id);
  if (!ap) { ap = { playerId: id, cause, fromStep: step, points: [], landedStep: null, landingDamage: 0, died: false }; run.airPaths.set(id, ap); run.airborne.push(ap); }
  return ap;
}
function stopWalk(run: RoundRun, i: number, arrived: boolean): void { run.walks[i].path.arrived = arrived; run.walks.splice(i, 1); }

/** Start a round: the orders become flights, walks and jumps (ammo and movement spent now unless prepaid). */
export function beginRound(initial: GameState, orders: RoundOrder[], opts: RoundOptions): RoundRun {
  let state = initial;
  const logs: string[] = [];
  const flights: RoundFlight[] = [];
  const live: LiveFlight[] = [];
  const pending: Array<{ live: LiveFlight; at: number }> = [];
  const pendingWalks: Array<{ playerId: number; target: Vec3; at: number }> = [];
  const pendingJumps: Array<{ playerId: number; jump: RoundJumpOrder; at: number }> = [];
  const walks: LiveWalk[] = [];
  const walkPaths: RoundWalkPath[] = [];
  const impacts: RoundImpactEvent[] = [];
  const airPaths = new Map<number, RoundAirbornePath>();
  const airborne: RoundAirbornePath[] = [];
  const stagger = Math.max(0, Math.floor(opts.launchStaggerSteps));
  const flightCap = opts.lostAfterSteps ?? Math.ceil(MAX_PROJECTILE_TIME / PHYS_DT);

  // ---- Carried flights (a fixed-length round's leftovers) continue first, in their order
  for (const c of opts.carried ?? []) {
    const f: RoundFlight = { index: flights.length, ownerId: c.ownerId, weaponKey: c.weaponKey, launchStep: c.delay, points: [], endStep: 0, outcome: 'skipped', hitPlanetIndex: -1, hitPoint: null, hitPlayerId: -1, submunitionOf: null };
    flights.push(f);
    pending.push({ live: { f, age0: c.age, pos: { ...c.pos }, vel: { ...c.vel }, cooldownIdx: c.cooldownIdx, transits: c.transits, boosted: new Set(c.boosted) }, at: c.delay });
  }

  // ---- Orders → flights, walks, jumps (ammo and movement spent now unless prepaid at planning)
  let orderIndex = 0;
  for (const order of orders) {
    const shooter = state.players.find(p => p.id === order.playerId);
    const at = orderIndex * stagger;
    let started = false;
    if (order.fire) {
      const params = order.fire.params;
      const idx = flights.length;
      const base: RoundFlight = { index: idx, ownerId: order.playerId, weaponKey: params.weaponKey, launchStep: 0, points: [], endStep: 0, outcome: 'skipped', hitPlanetIndex: -1, hitPoint: null, hitPlayerId: -1, submunitionOf: null };
      if (!shooter || !shooter.alive) { flights.push({ ...base, skipReason: 'shooter dead' }); }
      else if (WEAPONS[params.weaponKey].hitscan) { flights.push({ ...base, skipReason: 'hitscan weapons resolve outside the round' }); }
      else {
        const check: { ok: true } | { ok: false; reason: string } = opts.prepaid ? { ok: true } : checkFire(shooter, params.weaponKey);
        if (!check.ok) { flights.push({ ...base, skipReason: check.reason }); }
        else {
          if (!opts.prepaid) state = applyFireCost(state, shooter.id, params);
          let launch = order.fire.launch;
          if (!launch) { const p = createProjectile(shooter, state.planets, params); launch = { pos: p.pos, vel: p.vel }; }
          const f: RoundFlight = { ...base, launchStep: at };
          flights.push(f);
          pending.push({ live: { f, pos: { ...launch.pos }, vel: { ...launch.vel }, cooldownIdx: -1, transits: 0, boosted: new Set() }, at });
          logs.push(`${shooter.name} fires ${WEAPONS[params.weaponKey].name}`);
          started = true;
        }
      }
    }
    if (shooter && shooter.alive && order.jump) { pendingJumps.push({ playerId: shooter.id, jump: order.jump, at }); started = true; }
    else if (shooter && shooter.alive && order.walk) { pendingWalks.push({ playerId: shooter.id, target: vecNormalize(order.walk), at }); started = true; }
    if (started) orderIndex++;
  }
  pending.sort((a, b) => a.at - b.at || a.live.f.index - b.live.f.index);

  const geysers: GeyserSim[] = buildGeyserSims(state.hazards, state.planets);
  const lastLaunch = Math.max(0, ...pending.map(p => p.at), ...pendingWalks.map(w => w.at), ...pendingJumps.map(j => j.at));
  const maxSteps = opts.fixedSteps ?? opts.maxSteps ?? lastLaunch + flightCap + flightCap;   // flights, then time for launched units to land
  let grounded = groundedHitboxes(state.players, state.planets);
  let groundedDirty = false;

  return {
    state, opts, logs, flights, live, pending, pendingWalks, pendingJumps, walks, walkPaths, impacts, airPaths, airborne,
    geysers, flightCap, maxSteps, grounded, groundedDirty, step: 0, done: false,
  };
}

/**
 * Advance the round one physics step: launches due now, every live flight, this step's impacts in flight order,
 * walks, airborne units. Returns false (and does nothing) once the round has settled or reached its cap.
 */
export function stepRound(run: RoundRun): boolean {
  if (run.done) return false;
  if (run.step >= run.maxSteps) { run.done = true; return false; }
  const { opts, logs, flights, live, pending, pendingWalks, pendingJumps, walks, walkPaths, impacts, airPaths, airborne, geysers, flightCap } = run;
  const step = run.step;
  let state = run.state;
  let grounded = run.grounded;
  let groundedDirty = run.groundedDirty;
    // Launches due this step: flights, walks, jumps
    while (pending.length && pending[0].at <= step) {
      const { live: lf } = pending.shift()!;
      lf.f.launchStep = step;
      lf.f.points.push({ ...lf.pos });
      live.push(lf);
    }
    for (let i = pendingWalks.length - 1; i >= 0; i--) {
      const w = pendingWalks[i];
      if (w.at > step) continue;
      pendingWalks.splice(i, 1);
      const p = state.players.find(pl => pl.id === w.playerId);
      if (!p || !p.alive || p.airborne) continue;
      const path: RoundWalkPath = { playerId: w.playerId, fromStep: step, dirs: [], arrived: false };
      walkPaths.push(path);
      walks.push({ playerId: w.playerId, target: w.target, path });
    }
    for (let i = pendingJumps.length - 1; i >= 0; i--) {
      const j = pendingJumps[i];
      if (j.at > step) continue;
      pendingJumps.splice(i, 1);
      const p = state.players.find(pl => pl.id === j.playerId);
      if (!p || !p.alive || p.airborne) continue;
      state = { ...state, players: state.players.map(pl => pl.id !== j.playerId ? pl : { ...pl, airborne: true, airbornePos: { ...j.jump.launchPos }, airborneVel: { ...j.jump.launchVel }, airborneCause: 'jump' as const }) };
      airPathFor(run, j.playerId, step, 'jump');
      const wi = walks.findIndex(w => w.playerId === j.playerId);
      if (wi >= 0) stopWalk(run, wi, false);
      groundedDirty = true;
      logs.push(`${p.name} launches!`);
    }
    if (opts.fixedSteps === undefined && live.length === 0 && pending.length === 0 && walks.length === 0 && pendingWalks.length === 0 && pendingJumps.length === 0 && !state.players.some(p => p.alive && p.airborne)) {
      run.state = state; run.done = true;   // settled: the round ends here (the batch loop's break)
      return false;
    }

    if (groundedDirty) { grounded = groundedHitboxes(state.players, state.planets); groundedDirty = false; }
    const airHb = airborneHitboxes(state.players, state.planets);
    const planets = state.planets;

    // ---- Advance every live flight one step (collisions queued, resolved below in index order)
    type Ended = { lf: LiveFlight; kind: 'terrain' | 'player' | 'oob' | 'timeout'; planetIndex: number; point: Vec3 | null; playerId: number; airborneHit: boolean };
    const ended: Ended[] = [];
    for (const lf of live) {
      const r = rk4Step(lf.pos, lf.vel, PHYS_DT, planets);
      lf.pos = r.pos; lf.vel = r.vel;
      lf.f.points.push({ ...lf.pos });
      for (const g of geysers) {
        if (lf.boosted.has(g.id)) continue;
        if (dhypot(lf.pos.x - g.worldPos.x, lf.pos.y - g.worldPos.y, lf.pos.z - g.worldPos.z) < GEYSER_BOOST_RADIUS) {
          lf.vel = { x: lf.vel.x + g.normal.x * GEYSER_BOOST_VELOCITY, y: lf.vel.y + g.normal.y * GEYSER_BOOST_VELOCITY, z: lf.vel.z + g.normal.z * GEYSER_BOOST_VELOCITY };
          lf.boosted.add(g.id);
        }
      }
      let hit: Hitbox | null = null;
      for (const hb of grounded) { if (hb.playerId === lf.f.ownerId) continue; const dx = lf.pos.x - hb.center.x, dy = lf.pos.y - hb.center.y, dz = lf.pos.z - hb.center.z; if (dx * dx + dy * dy + dz * dz <= HIT_R2) { hit = hb; break; } }
      if (!hit) for (const hb of airHb) { if (hb.playerId === lf.f.ownerId) continue; const dx = lf.pos.x - hb.center.x, dy = lf.pos.y - hb.center.y, dz = lf.pos.z - hb.center.z; if (dx * dx + dy * dy + dz * dz <= HIT_R2) { hit = hb; break; } }
      if (hit) { ended.push({ lf, kind: 'player', planetIndex: hit.planetIndex, point: { ...hit.surfacePoint }, playerId: hit.playerId, airborneHit: hit.airborne }); continue; }
      if (lf.cooldownIdx >= 0) {
        const w = planets[lf.cooldownIdx];
        if (dhypot(lf.pos.x - w.position.x, lf.pos.y - w.position.y, lf.pos.z - w.position.z) > w.radius * WORMHOLE_COOLDOWN_DIST) lf.cooldownIdx = -1;
      }
      const c = checkCollision(lf.pos, planets, lf.cooldownIdx);
      if (c) { ended.push({ lf, kind: 'terrain', planetIndex: c.planetIndex, point: c.point, playerId: -1, airborneHit: false }); continue; }
      if (dhypot(lf.pos.x, lf.pos.y, lf.pos.z) > OUT_OF_BOUNDS) { ended.push({ lf, kind: 'oob', planetIndex: -1, point: null, playerId: -1, airborneHit: false }); continue; }
      if ((lf.age0 ?? 0) + lf.f.points.length - 1 >= flightCap) ended.push({ lf, kind: 'timeout', planetIndex: -1, point: null, playerId: -1, airborneHit: false });
    }

    // ---- Resolve this step's endings in flight order (deterministic)
    ended.sort((a, b) => a.lf.f.index - b.lf.f.index);
    for (const e of ended) {
      const { lf } = e;
      if (e.kind === 'oob' || e.kind === 'timeout') { endFlight(run, lf, step, 'lost', -1, null, -1); logs.push(`${WEAPONS[lf.f.weaponKey].name} lost in space`); continue; }
      const planet = e.planetIndex >= 0 ? state.planets[e.planetIndex] : null;
      if (e.kind === 'terrain' && planet && planet.type === 'wormhole') {
        lf.transits++;
        const linked = planet.linkedWormholeId != null ? state.planets.findIndex(p => p.id === planet.linkedWormholeId) : -1;
        if (lf.transits >= MAX_WORMHOLE_TRANSITS || linked < 0) { endFlight(run, lf, step, 'lost', e.planetIndex, e.point, -1); logs.push(`${WEAPONS[lf.f.weaponKey].name} torn apart by wormhole instability`); continue; }
        const exit = state.planets[linked];
        const speed = dhypot(lf.vel.x, lf.vel.y, lf.vel.z);
        const dir = speed > 0.001 ? { x: lf.vel.x / speed, y: lf.vel.y / speed, z: lf.vel.z / speed } : { x: 1, y: 0, z: 0 };
        lf.pos = { x: exit.position.x + dir.x * exit.radius * 1.5, y: exit.position.y + dir.y * exit.radius * 1.5, z: exit.position.z + dir.z * exit.radius * 1.5 };
        lf.vel = { x: dir.x * speed * WORMHOLE_SPEED_BOOST, y: dir.y * speed * WORMHOLE_SPEED_BOOST, z: dir.z * speed * WORMHOLE_SPEED_BOOST };
        lf.cooldownIdx = linked;
        lf.f.points[lf.f.points.length - 1] = { ...lf.pos };   // the step's point is the exit, as simulateFlight records it
        continue;
      }
      if (e.kind === 'terrain' && planet && planet.type === 'star') { endFlight(run, lf, step, 'lost', e.planetIndex, e.point, -1); logs.push(`${WEAPONS[lf.f.weaponKey].name} consumed by stellar fire`); continue; }
      // Impact (terrain or player) through the pure resolver; an airborne hit is an air burst (no crater)
      const point = e.point!;
      const planetIndex = e.planetIndex >= 0 ? e.planetIndex : nearestPlanet(point, state.planets);
      if (planetIndex < 0) { endFlight(run, lf, step, 'lost', -1, point, e.playerId); continue; }
      const projectile: ProjectileState = { pos: point, vel: lf.vel, thrustDir: { x: 0, y: 1, z: 0 }, thrustMag: 0, fuelRemaining: 0, weaponKey: lf.f.weaponKey, phase: 'ballistic', time: (lf.f.points.length - 1) * PHYS_DT, ownerId: lf.f.ownerId, lastWormholeId: null, wormholeTransits: lf.transits };
      const r = resolveImpact(state, { impactPoint: point, planetIndex, projectile, airBurst: e.airborneHit }, { createCraterImpl: opts.createCraterImpl });
      state = r.state;
      logs.push(...r.logs);
      impacts.push({ step, flightIndex: lf.f.index, planetIndex, point, airBurst: e.airborneHit, damages: r.damages.map(d => ({ playerId: d.playerId, damage: d.damage })), kills: r.kills, launched: r.launched, sheltered: r.sheltered, terrainChanged: !!r.terrain, resolution: r });
      endFlight(run, lf, step, e.kind === 'player' ? 'playerHit' : 'impact', planetIndex, point, e.playerId);
      groundedDirty = true;
      for (const id of r.launched) {
        airPathFor(run, id, step, 'knockback');
        const wi = walks.findIndex(w => w.playerId === id);
        if (wi >= 0) stopWalk(run, wi, false);   // knocked off your feet mid-walk
      }
      for (const id of r.kills) { const wi = walks.findIndex(w => w.playerId === id); if (wi >= 0) stopWalk(run, wi, false); }
      // Cluster bomblets join the round as flights of their own, staggered like the game does
      state = queueClusterSubmunitions(state, lf.f.weaponKey, point, planetIndex, lf.f.ownerId);
      let k = 0;
      for (;;) {
        const popped = popPendingCluster(state);
        state = popped.state;
        if (!popped.cluster) break;
        k++;
        const cf: RoundFlight = { index: flights.length, ownerId: lf.f.ownerId, weaponKey: popped.cluster.weaponKey, launchStep: step + CLUSTER_LAUNCH_DELAY_STEPS * k, points: [], endStep: 0, outcome: 'skipped', hitPlanetIndex: -1, hitPoint: null, hitPlayerId: -1, submunitionOf: lf.f.index };
        flights.push(cf);
        pending.push({ live: { f: cf, pos: { ...popped.cluster.pos }, vel: { ...popped.cluster.vel }, cooldownIdx: -1, transits: 0, boosted: new Set() }, at: cf.launchStep });
      }
      pending.sort((a, b) => a.at - b.at || a.live.f.index - b.live.f.index);
    }

    // ---- Planned walks advance along the surface at walking speed; hitboxes follow
    if (walks.length > 0) {
      const moved = new Map<number, Vec3>();
      for (let i = walks.length - 1; i >= 0; i--) {
        const w = walks[i];
        const p = state.players.find(pl => pl.id === w.playerId);
        if (!p || !p.alive || p.airborne) { stopWalk(run, i, false); continue; }
        const planet = state.planets[p.planetIndex];
        const cur = vecNormalize(p.surfaceDir);
        const dot = Math.max(-1, Math.min(1, cur.x * w.target.x + cur.y * w.target.y + cur.z * w.target.z));
        const remaining = dacos(dot);
        const stepAngle = (WALK_SPEED * PHYS_DT) / Math.max(0.01, planet.radius);
        let next: Vec3;
        let arrived = false;
        if (remaining <= stepAngle || remaining < 1e-6) { next = { ...w.target }; arrived = true; }
        else next = vecNormalize(vecSlerp(cur, w.target, stepAngle / remaining));
        w.path.dirs.push(next);
        moved.set(w.playerId, next);
        if (arrived) stopWalk(run, i, true);
      }
      if (moved.size > 0) {
        const players = state.players.map(p => { const d = moved.get(p.id); return d ? { ...p, surfaceDir: d } : p; });
        const carryables = state.carryables.map(c => { const d = c.carriedBy != null ? moved.get(c.carriedBy) : undefined; return d ? { ...c, surfaceDir: { ...d } } : c; });
        state = { ...state, players, carryables };
        groundedDirty = true;
      }
    }

    // ---- Airborne units (jumps and knockbacks) fly and land in the same clock
    if (state.players.some(p => p.alive && p.airborne)) {
      const players = state.players.map(p => {
        if (!p.alive || !p.airborne) return p;
        const res = updateAirbornePlayer(p, state.planets, PHYS_DT, state.config.fallDamageMultiplier ?? 1.0, state.config.fallDamageByCause);
        const ap = airPathFor(run, p.id, step, 'knockback');
        if (res.player.airbornePos) ap.points.push({ ...res.player.airbornePos });
        if (res.landed || !res.player.alive || !res.player.airborne) {
          ap.landedStep = step; ap.landingDamage = res.landingDamage; ap.died = !res.player.alive;
          airPaths.delete(p.id);
          groundedDirty = true;
          if (res.landed) logs.push(res.landingDamage > 0 ? `${p.name} crash lands (${Math.round(res.landingDamage)} dmg)` : `${p.name} lands`);
          if (!res.player.alive) logs.push(`${p.name} destroyed`);
        }
        return res.player;
      });
      state = { ...state, players };
    }
  
  run.state = state;
  run.grounded = grounded;
  run.groundedDirty = groundedDirty;
  run.step++;
  return true;
}

/** End the round where it stands and return its record: what is still flying is lost, walks stop where they are. */
export function finishRound(run: RoundRun): RoundRecord {
  run.done = true;
  const { live, pending, walks, walkPaths, flights, impacts, airborne, logs } = run;
  const step = run.step;
  // A fixed-length round hands over what is still in the air (decision 109): the next round's `carried`
  let inFlight: CarriedFlight[] | undefined;
  if (run.opts.fixedSteps !== undefined) {
    const carry = (lf: LiveFlight, delay: number): CarriedFlight => ({ ownerId: lf.f.ownerId, weaponKey: lf.f.weaponKey, pos: { ...lf.pos }, vel: { ...lf.vel }, age: (lf.age0 ?? 0) + Math.max(0, lf.f.points.length - 1), delay, cooldownIdx: lf.cooldownIdx, transits: lf.transits, boosted: [...lf.boosted].sort((a, b) => a - b) });
    inFlight = [...live].sort((a, b) => a.f.index - b.f.index).map(lf => carry(lf, 0));
    for (const lf of [...live]) { lf.f.endStep = step; lf.f.outcome = 'inFlight'; live.splice(live.indexOf(lf), 1); }
    for (const p of pending) { inFlight.push(carry(p.live, Math.max(0, p.at - step))); p.live.f.outcome = 'inFlight'; }
    pending.length = 0;
  }
  // Anything still flying when the cap hits is lost; walks still under way stop where they are
  for (const lf of [...live]) endFlight(run, lf, step, 'lost', -1, null, -1);
  for (const p of pending) { p.live.f.outcome = 'skipped'; p.live.f.skipReason = 'round ended before launch'; }
  for (let i = walks.length - 1; i >= 0; i--) stopWalk(run, i, false);
  return { flights, impacts, airborne, walks: walkPaths, steps: step, state: run.state, logs, ...(inFlight ? { inFlight } : {}) };
}

/** The whole round at once: begin, step until settled or capped, finish. */
export function simulateRound(initial: GameState, orders: RoundOrder[], opts: RoundOptions): RoundRecord {
  const run = beginRound(initial, orders, opts);
  while (stepRound(run)) { /* one physics step */ }
  return finishRound(run);
}

/** Stagger that makes every order run alone against the world the previous ones left (40.3). */
export const SEQUENTIAL_STAGGER_STEPS = Math.ceil(MAX_PROJECTILE_TIME / PHYS_DT) * (MAX_WORMHOLE_TRANSITS + 1) + 1;
